import { DateTime } from "luxon";

type CmpTimeZoneInfo = {
  label: string;
  timeZone: string;
};

const defaultTimeZone: CmpTimeZoneInfo = {
  label: "Eastern Time",
  timeZone: "America/Toronto",
};

const cmpTimeZoneByLabel: Record<string, CmpTimeZoneInfo> = {
  "(UTC-10:00) Hawaii-Aleutian Time": {
    label: "Hawaii-Aleutian Time",
    timeZone: "Pacific/Honolulu",
  },
  "(UTC-09:00) Alaska Time": {
    label: "Alaska Time",
    timeZone: "America/Anchorage",
  },
  "(UTC-08:00) Pacific Time": {
    label: "Pacific Time",
    timeZone: "America/Vancouver",
  },
  "(UTC-07:00) Mountain Time": {
    label: "Mountain Time",
    timeZone: "America/Edmonton",
  },
  "(UTC-07:00) Arizona Time": {
    label: "Arizona Time",
    timeZone: "America/Phoenix",
  },
  "(UTC-06:00) Central Time": {
    label: "Central Time",
    timeZone: "America/Winnipeg",
  },
  "(UTC-06:00) Saskatchewan Time": {
    label: "Saskatchewan Time",
    timeZone: "America/Regina",
  },
  "(UTC-05:00) Eastern Time": {
    label: "Eastern Time",
    timeZone: "America/Toronto",
  },
  "(UTC-05:00) Indiana Eastern Time": {
    label: "Indiana Eastern Time",
    timeZone: "America/Indiana/Indianapolis",
  },
  "(UTC-04:00) Atlantic Time": {
    label: "Atlantic Time",
    timeZone: "America/Halifax",
  },
  "(UTC-03:30) Newfoundland Time": {
    label: "Newfoundland Time",
    timeZone: "America/St_Johns",
  },
};

const labelByIanaTimeZone: Record<string, string> = Object.keys(cmpTimeZoneByLabel)
  .map((label) => cmpTimeZoneByLabel[label])
  .reduce((acc, timeZoneInfo) => {
    acc[timeZoneInfo.timeZone] = timeZoneInfo.label;
    return acc;
  }, {} as Record<string, string>);

const isValidIanaTimeZone = (timeZone: string): boolean => {
  if (!timeZone) {
    return false;
  }

  try {
    Intl.DateTimeFormat("en-US", { timeZone }).format(new Date());
    return true;
  } catch {
    return false;
  }
};

export const normalizeCmpTimeZone = (
  timeZone: string | null | undefined
): CmpTimeZoneInfo => {
  const trimmedTimeZone = (timeZone || "").trim();
  const mappedTimeZone = cmpTimeZoneByLabel[trimmedTimeZone];

  if (mappedTimeZone) {
    return mappedTimeZone;
  }

  if (isValidIanaTimeZone(trimmedTimeZone)) {
    return {
      label: labelByIanaTimeZone[trimmedTimeZone] || trimmedTimeZone.replace(/_/g, " "),
      timeZone: trimmedTimeZone,
    };
  }

  return defaultTimeZone;
};

export const utcMilliToDateTimeInputParts = (
  utcMilli: number,
  timeZone: string | null | undefined
): { date: string; time: string } => {
  const normalizedTimeZone = normalizeCmpTimeZone(timeZone).timeZone;
  const zonedDateTime = DateTime.fromMillis(utcMilli, { zone: normalizedTimeZone });

  if (!zonedDateTime.isValid) {
    return { date: "", time: "" };
  }

  return {
    date: zonedDateTime.toFormat("yyyy-MM-dd"),
    time: zonedDateTime.toFormat("HH:mm"),
  };
};

export const dateTimeInputPartsToUtcMilli = (
  date: string,
  time: string,
  timeZone: string | null | undefined
): number => {
  const normalizedTimeZone = normalizeCmpTimeZone(timeZone).timeZone;
  const zonedDateTime = DateTime.fromFormat(
    `${date || "1970-01-01"} ${time || "00:00"}`,
    "yyyy-MM-dd HH:mm",
    { zone: normalizedTimeZone }
  );

  return zonedDateTime.isValid ? zonedDateTime.toMillis() : NaN;
};

// Mirrors the backend rule: renewal and membership dates are UTC calendar
// dates (00:00 or 12:00 UTC) and show as that calendar day; every other
// invoice date shows in the chamber time zone.
const CMP_DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
export const isCmpUtcCalendarDateValue = (utcMilli: number): boolean => {
  const timeOfDay = ((utcMilli % CMP_DAY_IN_MILLISECONDS) + CMP_DAY_IN_MILLISECONDS) % CMP_DAY_IN_MILLISECONDS;
  return timeOfDay === 0 || timeOfDay === CMP_DAY_IN_MILLISECONDS / 2;
};
export const getCmpDateDisplayTimeZone = (
  utcMilli: number,
  timeZone: string | null | undefined,
): string => isCmpUtcCalendarDateValue(utcMilli) ? "UTC" : normalizeCmpTimeZone(timeZone).timeZone;

/**
 * Chamber Set Up "Last Saved October 30, 2026 at 3:14 PM" in the chamber time
 * zone, the same as Update Member (not the time zone of the staff computer).
 */
export const formatCmpLastSavedLabel = (utcMilli: number, timeZone: string | null | undefined): string =>
  `Last Saved ${new Intl.DateTimeFormat("en-US", {
    dateStyle: "long",
    timeStyle: "short",
    timeZone: normalizeCmpTimeZone(timeZone).timeZone,
  }).format(new Date(utcMilli))}`;
